'use client';

import React, { useEffect, useRef, useState } from 'react';

import { teko } from '@/assets/fonts';

type VariantType = 'primary' | 'secondary' | 'danger' | 'neutral';

type ColorScheme = {
  base: string;
  accent: string;
  hover: string;
  disabled: string;
  gradient: {
    stop1: string;
    stop2: string;
  };
};

type VariantsType = {
  [key in VariantType]: ColorScheme;
};

interface HexagonalButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: VariantType;
  color?: string;
  accentColor?: string;
  width?: number | string;
  height?: number;
  disabled?: boolean;
  label?: React.ReactNode;
  labelClassName?: string;
  textGradient?: string;
}

const VARIANTS: VariantsType = {
  primary: {
    base: '#3F4C89',
    accent: '#2B3A7E',
    hover: '#4F5C99',
    disabled: '#7A7A7A',
    gradient: {
      stop1: '#3F4C89',
      stop2: '#2B3A7E',
    },
  },
  secondary: {
    base: '#444444',
    accent: '#333333',
    hover: '#555555',
    disabled: '#7A7A7A',
    gradient: {
      stop1: '#444444',
      stop2: '#333333',
    },
  },
  danger: {
    base: '#3D2121',
    accent: '#FF5319',
    hover: '#4D2727',
    disabled: '#7A7A7A',
    gradient: {
      stop1: '#FF4D1D',
      stop2: '#DA292A',
    },
  },
  neutral: {
    base: '#363636',
    accent: '#6D6D6D',
    hover: '#464646',
    disabled: '#7A7A7A',
    gradient: {
      stop1: '#6D6D6D',
      stop2: '#6D6D6D',
    },
  },
};

const HexagonalButton: React.FC<HexagonalButtonProps> = ({
  children,
  onClick,
  variant = 'primary',
  color,
  accentColor,
  width,
  height = 52,
  className = '',
  disabled = false,
  label,
  labelClassName = '',
  textGradient,
  ...props
}) => {
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [svgWidth, setSvgWidth] = useState<number>((width as number) || 0);

  const colorScheme = VARIANTS[variant];

  const baseColor = disabled ? colorScheme.disabled : isHovered ? colorScheme.hover : color || colorScheme.base;
  const bottomAccent = disabled ? '#777777' : accentColor || colorScheme.accent;

  // Set default width to 100% if not specified
  const buttonWidth = width || '100%';

  // Get a standard viewBox width for the SVG (for consistency in shape)
  const viewBoxWidth = typeof width === 'number' ? width : 275;

  useEffect(() => {
    // Update the SVG width when the button is mounted and on resize
    if (buttonRef.current && buttonWidth === '100%') {
      const updateWidth = () => {
        if (buttonRef.current) {
          setSvgWidth(buttonRef.current.offsetWidth);
        }
      };

      updateWidth();
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    } else if (typeof buttonWidth === 'number') {
      setSvgWidth(buttonWidth);
    }
  }, [buttonWidth]);

  const textBgStyle = {
    background: textGradient || 'linear-gradient(180deg, rgba(255, 255, 255, 0.7) 0%, #FFFFFF 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    fontFamily: `${teko.style.fontFamily} !important`,
    lineHeight: '100%',
    letterSpacing: 0,
  };

  // Fix for unique IDs to prevent conflicts when multiple buttons are on same page
  const uniqueId = React.useId();

  return (
    <button
      ref={buttonRef}
      onClick={disabled ? undefined : onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={`relative flex items-center justify-center transition-all duration-300 bg-transparent border-none overflow-hidden
        ${disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:shadow-lg active:scale-95'} 
        ${className}
        ${isHovered && !disabled ? 'transform scale-105' : ''}`}
      style={{ width: buttonWidth, height }}
      disabled={disabled}
      aria-label={typeof label === 'string' ? label : undefined}
      {...props}>
      <svg
        width='100%'
        height={height}
        viewBox={`0 0 ${viewBoxWidth} ${height}`}
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
        className={`absolute inset-0 transition-all duration-300 ${disabled ? 'opacity-60' : ''}`}
        aria-hidden='true'
        preserveAspectRatio='none'>
        <mask id={`path-mask-${uniqueId}`} fill='white'>
          <path
            fillRule='evenodd'
            clipRule='evenodd'
            d={`M15 0H${viewBoxWidth - 8}L${viewBoxWidth} 8L${viewBoxWidth - 15} 52H8L0 44L15 0Z`}
          />
        </mask>
        <path
          fillRule='evenodd'
          clipRule='evenodd'
          d={`M15 0H${viewBoxWidth - 8}L${viewBoxWidth} 8L${viewBoxWidth - 15} 52H8L0 44L15 0Z`}
          fill='url(#paint0_linear-${uniqueId})'
        />
        <path
          fillRule='evenodd'
          clipRule='evenodd'
          d={`M15 0H${viewBoxWidth - 8}L${viewBoxWidth} 8L${viewBoxWidth - 15} 52H8L0 44L15 0Z`}
          fill={baseColor}
          className='transition-colors duration-300'
        />
        <path
          d={`M${viewBoxWidth - 8} 0L${viewBoxWidth - 7.293} -0.707107L${viewBoxWidth - 7.586} -1H${viewBoxWidth - 8}V0ZM15 0V-1H14.2844L14.0535 -0.322674L15 0ZM${viewBoxWidth} 8L${viewBoxWidth + 0.947} 8.32267L${viewBoxWidth + 1.147} 7.73324L${viewBoxWidth + 0.707} 7.29289L${viewBoxWidth} 8ZM${viewBoxWidth - 15} 52V53H${viewBoxWidth - 14.284}L${viewBoxWidth - 14.053} 52.3227L${viewBoxWidth - 15} 52ZM8 52L7.29289 52.7071L7.58579 53H8V52ZM0 44L-0.94651 43.6773L-1.14745 44.2668L-0.707107 44.7071L0 44ZM${viewBoxWidth - 8} -1H15V1H${viewBoxWidth - 8}V-1ZM${viewBoxWidth + 0.707} 7.29289L${viewBoxWidth - 7.293} -0.707107L${viewBoxWidth - 8.707} 0.707107L${viewBoxWidth - 0.293} 8.70711L${viewBoxWidth + 0.707} 7.29289ZM${viewBoxWidth - 0.947} 7.67733L${viewBoxWidth - 15.947} 51.6773L${viewBoxWidth - 14.053} 52.3227L${viewBoxWidth + 0.947} 8.32267L${viewBoxWidth - 0.947} 7.67733ZM8 53H${viewBoxWidth - 15}V51H8V53ZM-0.707107 44.7071L7.29289 52.7071L8.70711 51.2929L0.707107 43.2929L-0.707107 44.7071ZM14.0535 -0.322674L-0.94651 43.6773L0.94651 44.3227L15.9465 0.322674L14.0535 -0.322674Z`}
          fill={`url(#paint1_linear-${uniqueId})`}
          mask={`url(#path-mask-${uniqueId})`}
        />
        <mask
          id={`clip-mask-${uniqueId}`}
          style={{ maskType: 'alpha' }}
          maskUnits='userSpaceOnUse'
          x='0'
          y='0'
          width={viewBoxWidth}
          height={height}>
          <mask id={`path-inner-${uniqueId}`} fill='white'>
            <path
              fillRule='evenodd'
              clipRule='evenodd'
              d={`M15 0H${viewBoxWidth - 8}L${viewBoxWidth} 8L${viewBoxWidth - 15} 52H8L0 44L15 0Z`}
            />
          </mask>
          <path
            fillRule='evenodd'
            clipRule='evenodd'
            d={`M15 0H${viewBoxWidth - 8}L${viewBoxWidth} 8L${viewBoxWidth - 15} 52H8L0 44L15 0Z`}
            fill={`url(#paint2_linear-${uniqueId})`}
          />
          <path
            fillRule='evenodd'
            clipRule='evenodd'
            d={`M15 0H${viewBoxWidth - 8}L${viewBoxWidth} 8L${viewBoxWidth - 15} 52H8L0 44L15 0Z`}
            fill='black'
            fillOpacity='0.2'
          />
          <path
            d={`M${viewBoxWidth - 8} 0L${viewBoxWidth - 7.293} -0.707107L${viewBoxWidth - 7.586} -1H${viewBoxWidth - 8}V0ZM15 0V-1H14.2844L14.0535 -0.322674L15 0ZM${viewBoxWidth} 8L${viewBoxWidth + 0.947} 8.32267L${viewBoxWidth + 1.147} 7.73324L${viewBoxWidth + 0.707} 7.29289L${viewBoxWidth} 8ZM${viewBoxWidth - 15} 52V53H${viewBoxWidth - 14.284}L${viewBoxWidth - 14.053} 52.3227L${viewBoxWidth - 15} 52ZM8 52L7.29289 52.7071L7.58579 53H8V52ZM0 44L-0.94651 43.6773L-1.14745 44.2668L-0.707107 44.7071L0 44ZM${viewBoxWidth - 8} -1H15V1H${viewBoxWidth - 8}V-1ZM${viewBoxWidth + 0.707} 7.29289L${viewBoxWidth - 7.293} -0.707107L${viewBoxWidth - 8.707} 0.707107L${viewBoxWidth - 0.293} 8.70711L${viewBoxWidth + 0.707} 7.29289ZM${viewBoxWidth - 0.947} 7.67733L${viewBoxWidth - 15.947} 51.6773L${viewBoxWidth - 14.053} 52.3227L${viewBoxWidth + 0.947} 8.32267L${viewBoxWidth - 0.947} 7.67733ZM8 53H${viewBoxWidth - 15}V51H8V53ZM-0.707107 44.7071L7.29289 52.7071L8.70711 51.2929L0.707107 43.2929L-0.707107 44.7071ZM14.0535 -0.322674L-0.94651 43.6773L0.94651 44.3227L15.9465 0.322674L14.0535 -0.322674Z`}
            fill='white'
            fillOpacity='0.9'
            mask={`url(#path-inner-${uniqueId})`}
          />
        </mask>
        <g mask={`url(#clip-mask-${uniqueId})`}>
          <rect width={viewBoxWidth} height={height} fill={`url(#paint3_linear-${uniqueId})`} fillOpacity='0.5' />
          <rect
            width={viewBoxWidth}
            height={height}
            fillOpacity={isHovered && !disabled ? '0.2' : '0.1'}
            fill='white'
            className='transition-opacity duration-300'
          />
        </g>
        <path
          fillRule='evenodd'
          clipRule='evenodd'
          d={`M${viewBoxWidth - 14} 49H5L8 52H${viewBoxWidth - 15}L${viewBoxWidth - 14} 49Z`}
          fill={bottomAccent}
          className='transition-colors duration-300'
        />
        <path
          fillRule='evenodd'
          clipRule='evenodd'
          d='M110 3H168L171 0H107L110 3Z'
          fill='white'
          fillOpacity={disabled ? '0.05' : '0.1'}
          className='transition-opacity duration-300'
        />
        <defs>
          <linearGradient
            id={`paint0_linear-${uniqueId}`}
            x1={viewBoxWidth / 2}
            y1='0'
            x2={viewBoxWidth / 2}
            y2='52'
            gradientUnits='userSpaceOnUse'>
            <stop stopColor='#333333' stopOpacity='0.8' />
            <stop offset='1' stopColor='#111111' stopOpacity='0.9' />
          </linearGradient>
          <linearGradient
            id={`paint1_linear-${uniqueId}`}
            x1={viewBoxWidth / 2}
            y1='0'
            x2={viewBoxWidth / 2}
            y2='52'
            gradientUnits='userSpaceOnUse'>
            <stop stopColor='white' stopOpacity={isHovered && !disabled ? '0.2' : '0.1'} />
            <stop offset='1' stopColor={disabled ? '#777777' : bottomAccent} />
          </linearGradient>
          <linearGradient
            id={`paint2_linear-${uniqueId}`}
            x1={viewBoxWidth / 2}
            y1='0'
            x2={viewBoxWidth / 2}
            y2='52'
            gradientUnits='userSpaceOnUse'>
            <stop stopColor='#383838' stopOpacity='0.8' />
            <stop offset='1' stopColor='#1F1F1F' stopOpacity='0.9' />
          </linearGradient>
          <linearGradient
            id={`paint3_linear-${uniqueId}`}
            x1={viewBoxWidth / 2}
            y1='0'
            x2={viewBoxWidth / 2}
            y2='52'
            gradientUnits='userSpaceOnUse'>
            <stop
              stopColor={disabled ? '#777777' : colorScheme.gradient.stop1}
              stopOpacity={isHovered && !disabled ? '0.2' : '0'}
            />
            <stop offset='1' stopColor={disabled ? '#777777' : colorScheme.gradient.stop2} />
          </linearGradient>
        </defs>
      </svg>

      {/* Label with gradient text effect */}
      {label && (
        <span
          className={`relative z-10  font-regular text-[32px] tracking-wider transition-all duration-300 ${disabled ? 'opacity-60' : ''} ${labelClassName}`}
          style={{ ...textBgStyle, fontFamily: 'Teko' }}>
          {label}
        </span>
      )}

      {/* Children content - parent controls styling */}
      {children && <div className='relative z-10'>{children}</div>}
    </button>
  );
};

export default HexagonalButton;
